import React, { PureComponent } from 'react';
import _ from 'lodash';

type InputOrTextArea = HTMLInputElement | HTMLTextAreaElement;

interface TextInputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onInput' | 'defaultValue'> {
    onChange: (value: string, event: React.FocusEvent<InputOrTextArea>) => void;
    onInput?: (value: string, event: React.FormEvent<InputOrTextArea>) => void;
    maxWidth?: number;
    minWidth?: number;
    canBeTextarea?: boolean;
    defaultValue?: string | number;
}

interface TextInputState {
    isTextArea: boolean;
}

export default class TextInput extends PureComponent<TextInputProps, TextInputState> {
    static defaultProps = {
        maxWidth: 200,
        minWidth: 10,
    };

    state: TextInputState = {
        isTextArea: false,
    };

    _ref: InputOrTextArea | null = null;

    componentDidMount() {
        setTimeout(() => { this.resize(); }, 100);
    }

    componentDidUpdate() {
        setTimeout(() => { this.resize(); }, 10);
    }

    get isTextArea(): boolean {
        const { canBeTextarea, defaultValue } = this.props;
        const { isTextArea } = this.state;

        return defaultValue && String(defaultValue).includes('\n')
            ? true
            : !!(canBeTextarea && isTextArea);
    }

    resize() {
        const { minWidth, maxWidth } = this.props;

        if (!this._ref) return;

        if (this.isTextArea) {
            this._ref.style.height = Math.min(200, this._ref.scrollHeight) + 'px';
            return;
        }

        if (maxWidth) {
            this._ref.style.width = '';
            this._ref.style.width = _.clamp(this._ref.scrollWidth + 10, minWidth ?? 0, maxWidth) + 'px';
        }
    }

    handleRef = (elem: InputOrTextArea | null) => this._ref = elem;

    handleInput = (event: React.FormEvent<InputOrTextArea>) => {
        const { onInput } = this.props;
        this.resize();
        if (onInput) onInput(event.currentTarget.value, event);
    };

    handleChange = (event: React.FocusEvent<InputOrTextArea>) => {
        const { onChange, defaultValue } = this.props;
        const { value } = event.currentTarget;
        if (!value && defaultValue == null || value === defaultValue) return;
        onChange(value, event);
    };

    render() {
        const { onChange, onInput, defaultValue, minWidth, maxWidth, canBeTextarea, className, ...rest } = this.props;

        const props = {
            key: defaultValue,
            defaultValue: defaultValue,
            ...rest,
            ref: this.handleRef,
            onBlur: this.handleChange,
            onInput: this.handleInput,
            className: 'input',
        };

        let input = <input {...props} type='text' />;

        if (this.isTextArea) {
            input = <textarea
                {...(props as unknown as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
                style={{ ...props.style, minWidth: '20rem', fontFamily: 'courier, tahoma, arial', fontSize: '.75rem' }}
                rows={3}
                onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                    if (e.key === 'Tab') {
                        if (this._ref) insertTextAtCursor(this._ref, '    ');
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}
            />;
        }

        return <React.Fragment>
            {input}
            {(this.isTextArea || canBeTextarea) &&
                <label style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <input
                        type='checkbox'
                        checked={this.isTextArea}
                        title='Convert to a text area'
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            this.setState({ isTextArea: e.target.checked });
                            setTimeout(() => this._ref && this._ref.focus(), 20);
                        }}
                    />
                    <span>large</span>
                </label>
            }
        </React.Fragment>;
    }
}

function insertTextAtCursor(el: InputOrTextArea, text: string) {
    let val = el.value, endIndex;
    if (typeof el.selectionStart == 'number' && typeof el.selectionEnd == 'number') {
        endIndex = el.selectionEnd;
        el.value = val.slice(0, endIndex) + text + val.slice(endIndex);
        el.selectionStart = el.selectionEnd = endIndex + text.length;
    }
}
